/**
 * Calls on a client that is fetched on demand (PostHog, which loads only once
 * the visitor accepts analytics cookies; src/lib/analytics.ts).
 *
 *   - Before start(): a call is DROPPED, never held. Holding it would send
 *     what the visitor did before consenting the moment they consent.
 *   - While it loads: a call waits, and runs in order once the client
 *     arrives, so a signed-in visitor's identify() made in that gap is kept.
 *   - Once it has arrived: a call runs at once.
 *
 * A failed load (offline, or blocked by an extension) drops what waited and
 * leaves the client unstarted, so a later start() tries again.
 */
export function deferredClient<C>() {
  let client: C | null = null;
  let loading = false;
  let waiting: ((c: C) => void)[] = [];
  return {
    /** Run `fn` with the client: now, once it arrives, or not at all if nobody has asked for it. */
    run(fn: (c: C) => void): void {
      if (client) fn(client);
      else if (loading) waiting.push(fn);
    },
    /** Fetch the client with `load`, unless it is here or on its way. */
    start(load: () => Promise<C>): void {
      if (client || loading) return;
      loading = true;
      load().then(
        (c) => {
          client = c;
          loading = false;
          const queued = waiting;
          waiting = [];
          for (const fn of queued) fn(c);
        },
        () => {
          loading = false;
          waiting = [];
        },
      );
    },
  };
}
